/**
 * «حساب‌های بانکی هوپکس» (Phase 4): the destination accounts users transfer Toman to. Details are
 * immutable once created (a changed account is a new row — users never get swapped details under a
 * pending request); only status changes (ACTIVE / RESERVE / INACTIVE). Numbers are stored encrypted
 * and shown masked here; the full details appear only to a KYC2 user inside their own deposit
 * request. Bank names wait for the approved dataset (OPEN-13).
 */
import {
  AppContainer,
  Badge,
  Button,
  DataTable,
  Ltr,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt, type DepositMethod, type HopexAccount } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { DEPOSIT_METHOD } from '../../lib/money-labels.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { BankName } from '../../lib/ui.tsx';

const STATUS: Record<string, { label: string; tone: 'positive' | 'accent' | 'neutral' }> = {
  ACTIVE: { label: 'فعال', tone: 'positive' },
  RESERVE: { label: 'رزرو (پشتیبان)', tone: 'accent' },
  INACTIVE: { label: 'غیرفعال', tone: 'neutral' },
};

function Create({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [label, setLabel] = useState('');
  const [holder, setHolder] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [iban, setIban] = useState('');
  const [card, setCard] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [methods, setMethods] = useState<DepositMethod[]>([]);
  const [status, setStatus] = useState<'ACTIVE' | 'RESERVE'>('RESERVE');
  const [priority, setPriority] = useState('100');
  const ok =
    label.trim().length >= 2 &&
    holder.trim().length >= 2 &&
    methods.length > 0 &&
    /^\d{1,4}$/.test(priority);
  return (
    <CommandDialog
      title="افزودن حساب بانکی هوپکس"
      confirmLabel="ثبت حساب"
      canSubmit={ok}
      run={async (reason) => {
        await api.post('/hopex-bank-accounts', {
          label: label.trim(),
          holderName: holder.trim(),
          ...(bankCode.trim() ? { bankCode: bankCode.trim() } : {}),
          ...(iban.trim() ? { iban: iban.trim() } : {}),
          ...(card.trim() ? { card: card.trim() } : {}),
          ...(accountNo.trim() ? { accountNo: accountNo.trim() } : {}),
          methods,
          status,
          priority: Number(priority),
          reason,
        });
      }}
      onClose={onClose}
      onDone={onDone}
      data-create-hopex-account
    >
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        جزئیات پس از ثبت قابل ویرایش نیست. کارت به کارت به شماره‌ی کارت، و سایر روش‌ها به شبا نیاز
        دارند.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <TextField label="عنوان داخلی" value={label} onChange={setLabel} />
        <TextField label="نام صاحب حساب" value={holder} onChange={setHolder} />
        <TextField label="شبا" value={iban} onChange={setIban} ltr />
        <TextField label="شماره‌ی کارت" value={card} onChange={setCard} ltr />
        <TextField label="شماره‌ی حساب (اختیاری)" value={accountNo} onChange={setAccountNo} ltr />
        <TextField label="کد بانک (اختیاری)" value={bankCode} onChange={setBankCode} ltr />
      </div>
      <fieldset className="m-0 flex flex-wrap gap-3 border-0 p-0">
        <legend className="mb-1 text-[13px] text-fg-muted">روش‌های واریز پشتیبانی‌شده</legend>
        {(Object.keys(DEPOSIT_METHOD) as DepositMethod[]).map((m) => (
          <label key={m} className="flex items-center gap-1.5 text-[13px]">
            <input
              type="checkbox"
              checked={methods.includes(m)}
              onChange={(e) =>
                setMethods((x) => (e.target.checked ? [...x, m] : x.filter((y) => y !== m)))
              }
            />
            {DEPOSIT_METHOD[m]}
          </label>
        ))}
      </fieldset>
      <div className="grid gap-2 sm:grid-cols-2">
        <SelectField
          label="وضعیت اولیه"
          value={status}
          onChange={(v) => setStatus(v as 'ACTIVE' | 'RESERVE')}
          options={[
            { value: 'RESERVE', label: 'رزرو (پشتیبان)' },
            { value: 'ACTIVE', label: 'فعال' },
          ]}
        />
        <TextField
          label="اولویت (عدد کمتر = اولویت بالاتر)"
          value={priority}
          onChange={setPriority}
          ltr
          inputMode="numeric"
        />
      </div>
    </CommandDialog>
  );
}

export function HopexBanksPage() {
  const r = useRemote<{ items: HopexAccount[] }>('/hopex-bank-accounts');
  const [creating, setCreating] = useState(false);
  const [change, setChange] = useState<{ a: HopexAccount; to: HopexAccount['status'] } | null>(
    null,
  );
  return (
    <AppContainer>
      <PageHeader
        title="حساب‌های بانکی هوپکس"
        actions={
          <Button size="sm" onPress={() => setCreating(true)} data-open-create-hopex-account>
            افزودن حساب
          </Button>
        }
      />
      <Remote r={r}>
        {(d) => (
          <DataTable<HopexAccount>
            label="حساب‌های مقصد واریز"
            rows={d.items}
            density="compact"
            stickyFirst
            columns={[
              {
                id: 'label',
                header: 'عنوان',
                isRowHeader: true,
                cell: (a) => (
                  <span className="flex flex-col">
                    <span className="font-semibold">{a.label}</span>
                    <span className="text-[11px] text-fg-subtle">{a.holderName}</span>
                  </span>
                ),
              },
              { id: 'bank', header: 'بانک', cell: (a) => <BankName code={a.bankCode} /> },
              {
                id: 'nums',
                header: 'شبا / کارت / حساب',
                cell: (a) => (
                  <span className="flex flex-col font-mono text-[12px]">
                    {a.ibanMasked && <Ltr>{a.ibanMasked}</Ltr>}
                    {a.cardMasked && <Ltr>{a.cardMasked}</Ltr>}
                    {a.accountNoMasked && <Ltr>{a.accountNoMasked}</Ltr>}
                  </span>
                ),
              },
              {
                id: 'm',
                header: 'روش‌ها',
                cell: (a) => a.methods.map((m) => DEPOSIT_METHOD[m] ?? m).join('، '),
              },
              {
                id: 'st',
                header: 'وضعیت',
                cell: (a) => (
                  <Badge tone={STATUS[a.status]?.tone ?? 'neutral'}>
                    {STATUS[a.status]?.label ?? a.status}
                  </Badge>
                ),
              },
              {
                id: 'p',
                header: 'اولویت',
                numeric: true,
                cell: (a) => <bdi className="hx-num">{a.priority}</bdi>,
              },
              { id: 'at', header: 'ایجاد', cell: (a) => dt(a.createdAt) },
              {
                id: 'act',
                header: 'تغییر وضعیت',
                cell: (a) => (
                  <div className="flex flex-wrap gap-1">
                    {(['ACTIVE', 'RESERVE', 'INACTIVE'] as const)
                      .filter((s) => s !== a.status)
                      .map((s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant="ghost"
                          onPress={() => setChange({ a, to: s })}
                        >
                          {STATUS[s]?.label}
                        </Button>
                      ))}
                  </div>
                ),
              },
            ]}
            empty={
              <StateView
                compact
                kind="empty"
                title="حساب مقصدی ثبت نشده است"
                description="تا حساب فعالی ثبت نشود، روش‌های واریز تومان برای کاربران در دسترس نیست."
              />
            }
          />
        )}
      </Remote>
      {creating && (
        <Create
          onClose={() => setCreating(false)}
          onDone={() => (setCreating(false), r.reload())}
        />
      )}
      {change && (
        <CommandDialog
          title={`تغییر وضعیت «${change.a.label}»`}
          tone={change.to === 'INACTIVE' ? 'danger' : 'primary'}
          run={async (reason) => {
            await api.post(`/hopex-bank-accounts/${change.a.id}/status`, {
              status: change.to,
              expectedVersion: change.a.version,
              reason,
            });
          }}
          onClose={() => setChange(null)}
          onDone={() => (setChange(null), r.reload())}
        >
          <Facts
            rows={[
              ['وضعیت فعلی', STATUS[change.a.status]?.label ?? change.a.status],
              ['وضعیت جدید', STATUS[change.to]?.label ?? change.to],
              [
                'اثر',
                'درخواست‌های واریز ثبت‌شده همان مقصد قبلی را نگه می‌دارند؛ فقط درخواست‌های جدید تغییر می‌کنند.',
              ],
            ]}
          />
        </CommandDialog>
      )}
    </AppContainer>
  );
}
